import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStartQuiz, useSubmitAnswer } from '../../hooks/useQuiz';
import { useCourse } from '../../hooks/useCourses';
import { useToast } from '../../components/ui/Toast';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { PhaseDots } from '../../components/brand/PhaseBar';
import { Karti, type KartiMood } from '../../components/brand/Karti';
import { QUIZ_LINES, QUIZ_TIPS, pickLine, resultFor, useKartiLevel, type KartiLine } from '../../lib/karti';
import type { QuizVocabulary, AnswerResult } from '../../lib/api/quiz';
import { cn } from '../../lib/utils';

type QuizModeType = 'FLASHCARD' | 'MULTIPLE_CHOICE' | 'TEST';

const MODES: Array<{ mode: QuizModeType; name: string; desc: string; long: string }> = [
  {
    mode: 'FLASHCARD',
    name: 'Karteikarten',
    desc: 'Umdrehen und swipen',
    long: 'Karte umdrehen und selbst bewerten, ob du die Antwort gewusst hast. Ideal zum aktiven Abrufen.',
  },
  {
    mode: 'MULTIPLE_CHOICE',
    name: 'Multiple Choice',
    desc: 'Vier Antworten, eine stimmt',
    long: 'Vier Antworten, eine stimmt. Ideal zum Wiedererkennen und für den Einstieg.',
  },
  {
    mode: 'TEST',
    name: 'Test',
    desc: 'Antwort selbst tippen',
    long: 'Alle Fragen nacheinander tippen, am Ende korrigierst du selbst. Ideal für Prüfungssituationen.',
  },
];

const MODE_LABEL: Record<QuizModeType, string> = {
  FLASHCARD: 'Karteikarten',
  MULTIPLE_CHOICE: 'Multiple Choice',
  TEST: 'Test',
};

const NUMBER_WORDS = ['Null', 'Ein', 'Zwei', 'Drei', 'Vier', 'Fünf', 'Sechs', 'Sieben', 'Acht', 'Neun', 'Zehn', 'Elf', 'Zwölf'];

const pad = (n: number) => String(n).padStart(2, '0');

function termSizeClass(text: string, big: boolean) {
  const len = [...text].length;
  if (big) {
    if (len <= 1) return 'text-[96px] sm:text-[128px]';
    if (len <= 2) return 'text-[72px] sm:text-[84px]';
    if (len <= 4) return 'text-[56px] sm:text-[64px]';
    if (len <= 8) return 'text-[40px] sm:text-[44px]';
    return 'text-[28px] sm:text-[32px]';
  }
  if (len <= 2) return 'text-[84px] sm:text-[96px]';
  if (len <= 4) return 'text-[60px] sm:text-[72px]';
  if (len <= 8) return 'text-[40px] sm:text-[48px]';
  return 'text-[30px] sm:text-[36px]';
}

/* ------------------------------------------------------------------ */
/* Shared pieces                                                       */
/* ------------------------------------------------------------------ */

function QuizShell({ children }: { children: React.ReactNode }) {
  // Covers app header + tab bar while a session is running.
  return <div className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-paper">{children}</div>;
}

function QuizHeader({
  label,
  course,
  index,
  total,
  onClose,
}: {
  label: string;
  course?: string;
  index: number;
  total: number;
  onClose: () => void;
}) {
  return (
    <div className="mx-auto w-full max-w-xl px-5 pt-3">
      <div className="flex items-center gap-3.5">
        <button
          onClick={onClose}
          aria-label="Quiz beenden"
          className="press flex h-11 w-11 shrink-0 items-center justify-center rounded-btn border-2 border-ink bg-cream text-[15px] font-bold shadow-hard-sm"
        >
          ✕
        </button>
        <div className="min-w-0 flex-1">
          <div className="label-mono">{label}</div>
          <div className="truncate text-base font-bold">{course}</div>
        </div>
        <div className="font-mono text-[15px] font-bold">
          {pad(Math.min(index + 1, total))} / {pad(total)}
        </div>
      </div>
      <div className="mt-3 flex gap-1">
        {Array.from({ length: total }, (_, i) => (
          <span
            key={i}
            className={cn(
              'h-2 flex-1 rounded border-[1.5px] border-ink transition-colors duration-300',
              i < index ? 'bg-ink' : i === index ? 'bg-marker' : 'bg-cream'
            )}
          />
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function QuizPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const courseId = parseInt(id!);

  const { data: course } = useCourse(courseId);
  const startQuizMutation = useStartQuiz();
  const submitAnswerMutation = useSubmitAnswer();

  const [mode, setMode] = useState<QuizModeType>('FLASHCARD');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [vocabularies, setVocabularies] = useState<QuizVocabulary[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<AnswerResult[]>([]);
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [sessionStart, setSessionStart] = useState<number>(Date.now());
  const [finishedAt, setFinishedAt] = useState<number | null>(null);
  const [isFinished, setIsFinished] = useState(false);

  const currentVocab = vocabularies[currentIndex];
  const [kartiLevel] = useKartiLevel();
  const [reaction, setReaction] = useState<{ id: number; mood: KartiMood; text: string } | null>(null);

  // Karti reacts to answers with a short chip that hides itself after ~2.5 s.
  useEffect(() => {
    const n = answers.length;
    if (!sessionId || isFinished || n === 0) return;
    const last = answers[n - 1];
    let streak = 0;
    for (let i = n - 1; i >= 0 && answers[i].correct; i--) streak++;
    let pool: KartiLine[];
    if (!last.correct) pool = QUIZ_LINES.wrong;
    else if (n === Math.ceil(vocabularies.length / 2) && vocabularies.length >= 6) pool = QUIZ_LINES.half;
    else if (streak >= 5 && streak % 5 === 0) pool = QUIZ_LINES.streak5;
    else if (streak === 3) pool = QUIZ_LINES.streak3;
    else if (n % 3 === 0) pool = QUIZ_LINES.correct;
    else return;
    const line = pickLine(pool, kartiLevel, n);
    setReaction({ id: n, mood: line.mood, text: line.text });
    const t = setTimeout(() => setReaction(null), 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers.length]);

  const backToCourse = () => navigate(`/app/courses/${courseId}`);

  const finish = () => {
    setFinishedAt(Date.now());
    setIsFinished(true);
  };

  const handleStartQuiz = async () => {
    try {
      const result = await startQuizMutation.mutateAsync({
        courseId,
        dto: { mode },
      });
      setSessionId(result.sessionId);
      setVocabularies(result.vocabularies);
      setCurrentIndex(0);
      setAnswers([]);
      setStartTime(Date.now());
      setSessionStart(Date.now());
      setFinishedAt(null);
      setIsFinished(false);
      setReaction(null);
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Fehler beim Starten', 'error');
    }
  };

  const advance = () => {
    if (currentIndex < vocabularies.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setStartTime(Date.now());
    } else {
      finish();
    }
  };

  /** Flashcard: submit + advance. Resolves false when the request failed. */
  const handleFlashcardSubmit = async (correct: boolean): Promise<boolean> => {
    if (!sessionId || !currentVocab) return false;
    const timeTaken = Date.now() - startTime;
    try {
      // The backend compares userAnswer exactly: send the real answer if known, else empty.
      const userAnswer = correct ? currentVocab.answer : '';
      const result = await submitAnswerMutation.mutateAsync({
        sessionId,
        vocabId: currentVocab.id,
        userAnswer,
        timeTaken,
      });
      setAnswers((prev) => [...prev, result]);
      advance();
      return true;
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Fehler beim Absenden', 'error');
      return false;
    }
  };

  /** Multiple choice: submit only (feedback is shown first, advance happens afterwards). */
  const handleMultipleChoiceAnswer = async (userAnswer: string): Promise<AnswerResult | null> => {
    if (!sessionId || !currentVocab) return null;
    const timeTaken = Date.now() - startTime;
    try {
      const result = await submitAnswerMutation.mutateAsync({
        sessionId,
        vocabId: currentVocab.id,
        userAnswer,
        timeTaken,
      });
      setAnswers((prev) => [...prev, result]);
      return result;
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Fehler beim Absenden', 'error');
      return null;
    }
  };

  /* ---------------- start screen ---------------- */
  if (!sessionId) {
    const selected = MODES.find((m) => m.mode === mode)!;
    return (
      <div className="mx-auto max-w-xl space-y-5">
        <button
          onClick={backToCourse}
          className="press flex h-11 items-center rounded-btn border-2 border-ink bg-cream px-3.5 text-[15px] font-bold shadow-hard-sm"
        >
          ‹ Kurs
        </button>
        <div>
          <div className="label-mono">Lernsession · 15 Vokabeln</div>
          <h1 className="mt-1 text-[34px] font-extrabold leading-none">{course?.name ?? 'Quiz'}</h1>
        </div>

        <div className="space-y-3">
          {MODES.map((m, i) => (
            <button
              key={m.mode}
              onClick={() => setMode(m.mode)}
              aria-pressed={mode === m.mode}
              className={cn(
                'press flex min-h-[68px] w-full items-center gap-3.5 rounded-list border-2.5 border-ink px-4 text-left shadow-hard',
                mode === m.mode ? 'bg-marker' : 'bg-cream'
              )}
            >
              <span className="font-mono text-xs font-bold">{pad(i + 1)}</span>
              <span className="flex-1">
                <span className="block text-[17px] font-bold">{m.name}</span>
                <span className="block text-[13px] text-body">{m.desc}</span>
              </span>
              <span className="text-xl font-extrabold">{mode === m.mode ? '✓' : '→'}</span>
            </button>
          ))}
        </div>

        <p className="text-sm text-body">{selected.long}</p>

        <Button onClick={handleStartQuiz} disabled={startQuizMutation.isPending} size="lg" className="w-full">
          {startQuizMutation.isPending ? 'Wird vorbereitet...' : 'Los →'}
        </Button>
      </div>
    );
  }

  /* ---------------- result ---------------- */
  if (isFinished) {
    return (
      <ResultView
        answers={answers}
        vocabularies={vocabularies}
        mode={mode}
        durationMs={(finishedAt ?? Date.now()) - sessionStart}
        onRetry={handleStartQuiz}
        retrying={startQuizMutation.isPending}
        onBack={backToCourse}
      />
    );
  }

  /* ---------------- running sessions ---------------- */
  const chip = reaction && (
    <div
      key={reaction.id}
      className="pointer-events-none fixed inset-x-0 bottom-6 z-[70] flex justify-center px-4 sm:bottom-8"
    >
      <div className="animate-karti-in flex items-center gap-2 rounded-full border-2.5 border-ink bg-cream py-1 pl-1.5 pr-4 shadow-hard-sm">
        <Karti size={36} mood={reaction.mood} />
        <span className="text-[14px] font-bold">{reaction.text}</span>
      </div>
    </div>
  );

  if (mode === 'FLASHCARD') {
    return (
      <>
      {chip}
      <FlashcardMode
        course={course?.name}
        vocabularies={vocabularies}
        currentIndex={currentIndex}
        onSubmit={handleFlashcardSubmit}
        onClose={backToCourse}
      />
      </>
    );
  }

  if (mode === 'TEST') {
    return (
      <>
      {chip}
      <TestMode
        course={course?.name}
        sessionId={sessionId}
        vocabularies={vocabularies}
        onFinish={(results) => {
          setAnswers(results);
          finish();
        }}
        submitAnswerMutation={submitAnswerMutation}
        onClose={backToCourse}
      />
      </>
    );
  }

  return (
    <>
    {chip}
    <MultipleChoiceMode
      key={currentVocab.id}
      course={course?.name}
      vocab={currentVocab}
      currentIndex={currentIndex}
      total={vocabularies.length}
      onAnswer={handleMultipleChoiceAnswer}
      onNext={advance}
      onClose={backToCourse}
    />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Flashcards                                                          */
/* ------------------------------------------------------------------ */

function FlashcardMode({
  course,
  vocabularies,
  currentIndex,
  onSubmit,
  onClose,
}: {
  course?: string;
  vocabularies: QuizVocabulary[];
  currentIndex: number;
  onSubmit: (correct: boolean) => Promise<boolean>;
  onClose: () => void;
}) {
  const vocab = vocabularies[currentIndex];
  const total = vocabularies.length;
  const [known, setKnown] = useState(0);
  const [again, setAgain] = useState(0);

  const [flipped, setFlipped] = useState(false);
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const start = useRef<{ x: number; moved: boolean } | null>(null);
  const crossed = useRef(false);

  // reset between cards (no animation)
  useEffect(() => {
    setFlipped(false);
    setDx(0);
    setBusy(false);
  }, [vocab?.id]);

  const commit = useCallback(
    async (dir: 1 | -1) => {
      if (busy) return;
      setBusy(true);
      setDragging(false);
      setDx(dir * 520);
      await new Promise((r) => setTimeout(r, 320));
      const ok = await onSubmit(dir === 1);
      if (ok) {
        if (dir === 1) setKnown((k) => k + 1);
        else setAgain((a) => a + 1);
      } else {
        setDx(0);
        setBusy(false);
      }
    },
    [busy, onSubmit]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') commit(1);
      else if (e.key === 'ArrowLeft') commit(-1);
      else if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        if (!busy) setFlipped((f) => !f);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [commit, busy]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (busy) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    start.current = { x: e.clientX, moved: false };
    crossed.current = false;
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!start.current || busy) return;
    const d = e.clientX - start.current.x;
    if (Math.abs(d) >= 5) start.current.moved = true;
    if (start.current.moved) {
      setDragging(true);
      setDx(d);
    }
  };
  const onPointerUp = () => {
    const s = start.current;
    start.current = null;
    if (!s || busy) return;
    if (!s.moved) {
      setFlipped((f) => !f);
      setDragging(false);
      return;
    }
    setDragging(false);
    if (Math.abs(dx) > 90) commit(dx > 0 ? 1 : -1);
    else setDx(0);
  };

  const progress = Math.min(Math.abs(dx) / 260, 1);
  const next1 = vocabularies[currentIndex + 1];
  const next2 = vocabularies[currentIndex + 2];
  const cardSize = 'h-[min(410px,100%)] aspect-[296/410] max-w-full';

  return (
    <QuizShell>
      <QuizHeader label="Karteikarten" course={course} index={currentIndex} total={total} onClose={onClose} />

      <div className="flex min-h-0 flex-1 items-center justify-center px-5 py-6">
        <div className={cn('relative', cardSize)} style={{ touchAction: 'pan-y' }}>
          {/* behind cards */}
          {next2 && (
            <div
              className="absolute inset-0 rounded-flash border-[3px] border-ink bg-cream shadow-hard-6"
              style={{ transform: 'translateY(26px) scale(.88)' }}
            />
          )}
          {next1 && (
            <div
              className="absolute inset-0 flex items-center justify-center rounded-flash border-[3px] border-ink bg-cream shadow-hard-6"
              style={{
                transform: `translateY(${13 - 13 * progress}px) scale(${0.94 + 0.06 * progress})`,
                transition: dragging ? 'none' : 'transform 380ms cubic-bezier(.2,.8,.2,1)',
              }}
            >
              <span className="font-term text-[64px] font-black text-divider sm:text-[80px]">
                {[...next1.question].length > 3 ? '' : next1.question}
              </span>
            </div>
          )}

          {/* front card */}
          <div
            className="absolute inset-0 cursor-grab select-none active:cursor-grabbing"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            style={{
              transform: `translateX(${dx}px) translateY(${Math.abs(dx) * 0.06}px) rotate(${dx / 16}deg)`,
              transition: dragging ? 'none' : `transform ${busy ? 320 : 380}ms cubic-bezier(.2,.8,.2,1)`,
            }}
          >
            <div className="perspective-1000 h-full w-full">
              <div
                className="relative h-full w-full"
                style={{
                  transformStyle: 'preserve-3d',
                  transform: `rotateY(${flipped ? 180 : 0}deg)`,
                  transition: 'transform 550ms cubic-bezier(.4,.1,.2,1)',
                }}
              >
                {/* Front */}
                <div
                  className="absolute inset-0 flex flex-col items-center justify-between rounded-flash border-[3px] border-ink bg-cream p-5 shadow-hard-6"
                  style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
                  aria-hidden={flipped}
                >
                  <div className="label-mono self-start">Frage → Antwort</div>
                  <div
                    className={cn(
                      'max-w-full break-words text-center font-term font-black leading-none',
                      termSizeClass(vocab.question, true)
                    )}
                  >
                    {vocab.question}
                  </div>
                  <div className="text-sm text-muted-foreground">Tippen zum Umdrehen</div>
                </div>
                {/* Back */}
                <div
                  className="absolute inset-0 flex flex-col justify-between rounded-flash border-[3px] border-ink bg-marker p-5 shadow-hard-6"
                  style={{
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                    transform: 'rotateY(180deg)',
                  }}
                  aria-hidden={!flipped}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="label-mono !text-ink">Antwort</span>
                    <span className="font-term text-[22px] font-black">{[...vocab.question].length > 6 ? '' : vocab.question}</span>
                  </div>
                  <div
                    className={cn(
                      'break-words text-center font-extrabold leading-tight tracking-[-0.03em]',
                      [...vocab.answer].length > 14 ? 'text-3xl' : 'text-[44px] sm:text-[52px]'
                    )}
                  >
                    {vocab.answer}
                  </div>
                  <div className="border-t-2 border-ink pt-3 font-mono text-[11px] font-bold uppercase tracking-[0.06em]">
                    Phase {vocab.phase} · Score {vocab.score}
                  </div>
                </div>
              </div>
            </div>

            {/* stamps */}
            <div
              className="pointer-events-none absolute left-3 top-4 -rotate-[14deg] rounded-tile border-[3px] border-ink bg-correct px-3 py-1.5 text-[22px] font-extrabold"
              style={{ opacity: Math.min(Math.max(dx / 90, 0), 1) }}
            >
              GEWUSST
            </div>
            <div
              className="pointer-events-none absolute right-3 top-4 rotate-[14deg] rounded-tile border-[3px] border-ink bg-wrong px-3 py-1.5 text-[22px] font-extrabold"
              style={{ opacity: Math.min(Math.max(-dx / 90, 0), 1) }}
            >
              NOCHMAL
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-xl space-y-3.5 px-5 pb-8">
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => commit(-1)}
            disabled={busy}
            className="press flex h-[60px] items-center justify-center gap-2.5 rounded-[18px] border-2.5 border-ink bg-cream text-base font-bold shadow-hard disabled:opacity-60"
          >
            <span className="h-3 w-3 rounded-full border-2 border-ink bg-wrong" />
            Nochmal
          </button>
          <button
            onClick={() => commit(1)}
            disabled={busy}
            className="press flex h-[60px] items-center justify-center gap-2.5 rounded-[18px] border-2.5 border-ink bg-ink text-base font-bold text-paper shadow-hard-ink disabled:opacity-60"
          >
            Gewusst
            <span className="h-3 w-3 rounded-full border-2 border-paper bg-correct" />
          </button>
        </div>
        <div className="text-center font-mono text-xs font-bold uppercase text-muted-foreground">
          ← Nochmal {again} &nbsp; Gewusst {known} →
        </div>
      </div>
    </QuizShell>
  );
}

/* ------------------------------------------------------------------ */
/* Multiple choice                                                     */
/* ------------------------------------------------------------------ */

const TIMER_MS = 10000;
const ADVANCE_MS = 1600;

function MultipleChoiceMode({
  course,
  vocab,
  currentIndex,
  total,
  onAnswer,
  onNext,
  onClose,
}: {
  course?: string;
  vocab: QuizVocabulary;
  currentIndex: number;
  total: number;
  onAnswer: (answer: string) => Promise<AnswerResult | null>;
  onNext: () => void;
  onClose: () => void;
}) {
  const [picked, setPicked] = useState<string | null>(null); // '' = timeout
  const [result, setResult] = useState<AnswerResult | null>(null);
  const [remaining, setRemaining] = useState(1);
  const answered = useRef(false);

  const submit = useCallback(
    async (answer: string) => {
      if (answered.current) return;
      answered.current = true;
      setPicked(answer);
      const res = await onAnswer(answer);
      if (!res) {
        // failed: allow retry
        answered.current = false;
        setPicked(null);
        return;
      }
      setResult(res);
      setTimeout(onNext, ADVANCE_MS);
    },
    [onAnswer, onNext]
  );

  // linear 10 s timer; freezes once answered, timeout counts as wrong
  useEffect(() => {
    if (picked !== null) return;
    const t0 = Date.now() - (1 - remaining) * TIMER_MS;
    const iv = setInterval(() => {
      const r = Math.max(1 - (Date.now() - t0) / TIMER_MS, 0);
      setRemaining(r);
      if (r <= 0) {
        clearInterval(iv);
        submit('');
      }
    }, 100);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked, submit]);

  const done = picked !== null;
  const wasCorrect = result?.correct ?? false;

  return (
    <QuizShell>
      <QuizHeader label="Multiple Choice" course={course} index={currentIndex} total={total} onClose={onClose} />
      <div className="mx-auto mt-3 h-3 w-full max-w-xl px-5">
        <div className="h-3 overflow-hidden rounded-[7px] border-2 border-ink bg-cream">
          <div className="h-full bg-ink" style={{ width: `${remaining * 100}%` }} />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-5">
        <div className="label-mono">Was bedeutet</div>
        <div
          className={cn(
            'max-w-full break-words text-center font-term font-black leading-[1.05]',
            termSizeClass(vocab.question, false)
          )}
        >
          {vocab.question}
        </div>
      </div>

      <div className="mx-auto w-full max-w-xl space-y-3 px-5 pb-8">
        {vocab.options?.map((option, i) => {
          const isPicked = picked === option;
          const isCorrect = option === vocab.answer;
          const showCorrect = done && isCorrect;
          const showWrong = done && isPicked && !isCorrect;
          return (
            <button
              key={i}
              onClick={() => submit(option)}
              disabled={done}
              className={cn(
                'press flex h-[62px] w-full items-center gap-3.5 rounded-list border-2.5 border-ink px-3 text-left shadow-hard transition-[background-color,opacity] duration-200',
                showCorrect ? 'bg-correct' : showWrong ? 'bg-wrong animate-shake' : 'bg-cream',
                done && !showCorrect && !showWrong && 'opacity-40',
                'disabled:pointer-events-none'
              )}
            >
              <span
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-key border-2 border-ink font-mono text-sm font-bold',
                  showCorrect || showWrong ? 'bg-ink text-paper' : 'bg-paper'
                )}
              >
                {String.fromCharCode(65 + i)}
              </span>
              <span className="flex-1 truncate text-lg font-bold">{option}</span>
              <span className="w-5 text-lg font-extrabold">{showCorrect ? '✓' : showWrong ? '✕' : ''}</span>
            </button>
          );
        })}
      </div>

      {result && (
        <div
          className={cn(
            'absolute inset-x-3 bottom-3 mx-auto max-w-xl animate-sheet rounded-b-[38px] rounded-t-[28px] border-[3px] border-ink px-[18px] pb-[22px] pt-4',
            wasCorrect ? 'bg-correct' : 'bg-wrong'
          )}
          role="status"
        >
          <div className="text-2xl font-extrabold">{wasCorrect ? 'Richtig!' : 'Knapp daneben.'}</div>
          <div className="font-mono text-[13px] font-bold uppercase">
            {!wasCorrect && <>Richtig: {result.correctAnswer} · </>}Phase {result.oldPhase} → {result.newPhase}
          </div>
        </div>
      )}
    </QuizShell>
  );
}

/* ------------------------------------------------------------------ */
/* Test mode                                                           */
/* ------------------------------------------------------------------ */

interface TestAnswer {
  vocabId: number;
  question: string;
  correctAnswer: string;
  userAnswer: string;
}

function TestMode({
  course,
  sessionId,
  vocabularies,
  onFinish,
  submitAnswerMutation,
  onClose,
}: {
  course?: string;
  sessionId: string;
  vocabularies: QuizVocabulary[];
  onFinish: (results: AnswerResult[]) => void;
  submitAnswerMutation: any;
  onClose: () => void;
}) {
  const { showToast } = useToast();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<TestAnswer[]>([]);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [phase, setPhase] = useState<'answering' | 'reviewing'>('answering');
  const [reviewAnswers, setReviewAnswers] = useState<Array<TestAnswer & { isCorrect: boolean }>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentVocab = vocabularies[currentIndex];
  const totalQuestions = vocabularies.length;

  const handleNext = () => {
    const answer: TestAnswer = {
      vocabId: currentVocab.id,
      question: currentVocab.question,
      correctAnswer: currentVocab.answer,
      userAnswer: currentAnswer.trim(),
    };

    const newAnswers = [...userAnswers, answer];
    setUserAnswers(newAnswers);
    setCurrentAnswer('');

    if (currentIndex < totalQuestions - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setReviewAnswers(newAnswers.map((a) => ({ ...a, isCorrect: false })));
      setPhase('reviewing');
    }
  };

  const handleToggleCorrect = (index: number) => {
    const updated = [...reviewAnswers];
    updated[index].isCorrect = !updated[index].isCorrect;
    setReviewAnswers(updated);
  };

  const handleSubmitAll = async () => {
    setIsSubmitting(true);
    try {
      const results: AnswerResult[] = [];
      for (const answer of reviewAnswers) {
        const userAnswer = answer.isCorrect ? answer.correctAnswer : '';
        const r = await submitAnswerMutation.mutateAsync({
          sessionId,
          vocabId: answer.vocabId,
          userAnswer,
          timeTaken: 0,
        });
        results.push(r);
      }
      showToast('Test abgeschlossen!', 'success');
      onFinish(results);
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Fehler beim Absenden', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (phase === 'answering') {
    return (
      <QuizShell>
        <QuizHeader label="Test" course={course} index={currentIndex} total={totalQuestions} onClose={onClose} />
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-5">
          <div className="label-mono">Was bedeutet</div>
          <div
            className={cn(
              'max-w-full break-words text-center font-term font-black leading-[1.05]',
              termSizeClass(currentVocab.question, false)
            )}
          >
            {currentVocab.question}
          </div>
        </div>
        <div className="mx-auto w-full max-w-xl space-y-3 px-5 pb-8">
          <Input
            type="text"
            value={currentAnswer}
            onChange={(e) => setCurrentAnswer(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && currentAnswer.trim() && handleNext()}
            placeholder="Antwort eingeben..."
            aria-label="Deine Antwort"
            autoFocus
            autoCapitalize="off"
            autoCorrect="off"
          />
          <p className="text-center text-[13px] text-muted-foreground">
            Weißt du es nicht, lass das Feld leer.
          </p>
          <Button onClick={handleNext} size="lg" className="w-full">
            {currentIndex < totalQuestions - 1 ? 'Nächste Frage →' : 'Zur Korrektur →'}
          </Button>
        </div>
      </QuizShell>
    );
  }

  const correctCount = reviewAnswers.filter((a) => a.isCorrect).length;
  return (
    <QuizShell>
      <div className="mx-auto flex w-full max-w-xl items-center gap-3.5 px-5 pt-3">
        <button
          onClick={onClose}
          aria-label="Quiz beenden"
          className="press flex h-11 w-11 shrink-0 items-center justify-center rounded-btn border-2 border-ink bg-cream text-[15px] font-bold shadow-hard-sm"
        >
          ✕
        </button>
        <div className="min-w-0 flex-1">
          <div className="label-mono">Test · Korrektur</div>
          <div className="truncate text-base font-bold">Tippe, um richtig / falsch zu setzen</div>
        </div>
        <div className="font-mono text-[15px] font-bold">
          {pad(correctCount)} / {pad(reviewAnswers.length)}
        </div>
      </div>

      <div className="mx-auto min-h-0 w-full max-w-xl flex-1 space-y-3 overflow-y-auto px-5 py-4">
        {reviewAnswers.map((answer, index) => (
          <button
            key={answer.vocabId}
            onClick={() => handleToggleCorrect(index)}
            className={cn(
              'press flex w-full items-start gap-3 rounded-list border-2.5 border-ink p-3.5 text-left shadow-hard',
              answer.isCorrect ? 'bg-correct' : 'bg-cream'
            )}
          >
            <span
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-key border-2 border-ink font-extrabold',
                answer.isCorrect ? 'bg-ink text-paper' : 'bg-wrong'
              )}
            >
              {answer.isCorrect ? '✓' : '✕'}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-term text-[22px] font-black leading-tight">{answer.question}</span>
              <span className="mt-1 block text-sm">
                <span className="text-muted-foreground">Deine Antwort: </span>
                <span className={answer.userAnswer ? 'font-bold' : 'italic text-muted-foreground'}>
                  {answer.userAnswer || '(leer)'}
                </span>
              </span>
              <span className="block text-sm">
                <span className="text-muted-foreground">Richtig: </span>
                <span className="font-bold">{answer.correctAnswer}</span>
              </span>
            </span>
          </button>
        ))}
      </div>

      <div className="mx-auto grid w-full max-w-xl grid-cols-2 gap-3 px-5 pb-8 pt-2">
        <Button onClick={() => setPhase('answering')} variant="outline" size="lg">
          ‹ Zurück
        </Button>
        <Button onClick={handleSubmitAll} disabled={isSubmitting} size="lg">
          {isSubmitting ? 'Speichern...' : 'Abschließen'}
        </Button>
      </div>
    </QuizShell>
  );
}

/* ------------------------------------------------------------------ */
/* Result                                                              */
/* ------------------------------------------------------------------ */

function ResultView({
  answers,
  vocabularies,
  mode,
  durationMs,
  onRetry,
  retrying,
  onBack,
}: {
  answers: AnswerResult[];
  vocabularies: QuizVocabulary[];
  mode: QuizModeType;
  durationMs: number;
  onRetry: () => void;
  retrying: boolean;
  onBack: () => void;
}) {
  const total = answers.length;
  const correctCount = answers.filter((a) => a.correct).length;
  const mistakes = total - correctCount;
  const pct = total ? Math.round((correctCount / total) * 100) : 0;
  const up = answers.filter((a) => a.newPhase > a.oldPhase).length;

  const reduced =
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const [shown, setShown] = useState(reduced ? correctCount : 0);
  const [settled, setSettled] = useState(!!reduced);
  const [replay, setReplay] = useState(0);

  useEffect(() => {
    if (reduced) {
      setShown(correctCount);
      setSettled(true);
      return;
    }
    setShown(0);
    setSettled(false);
    let raf = 0;
    const timeout = setTimeout(() => {
      const t0 = performance.now();
      const tick = (now: number) => {
        const t = Math.min((now - t0) / 1200, 1);
        setShown(Math.round(correctCount * (1 - Math.pow(1 - t, 3))));
        if (t < 1) raf = requestAnimationFrame(tick);
        else setSettled(true);
      };
      raf = requestAnimationFrame(tick);
    }, 400);
    return () => {
      clearTimeout(timeout);
      cancelAnimationFrame(raf);
    };
  }, [correctCount, reduced, replay]);

  const secs = Math.max(Math.round(durationMs / 1000), 0);
  const mmss = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
  const [kartiLevel] = useKartiLevel();
  const kartiResult = resultFor(pct);
  const kartiLine = pickLine(kartiResult.pool, kartiLevel, total + correctCount);
  const kartiTip = kartiResult.tip ? pickLine(QUIZ_TIPS, kartiLevel, total + correctCount) : null;
  const word = (n: number) => (n <= 12 ? NUMBER_WORDS[n] : String(n));

  let headline: React.ReactNode;
  if (up === 0) {
    headline = (
      <>
        Diesmal ist kein Wort <span className="highlight">aufgestiegen.</span>
      </>
    );
  } else if (up === 1) {
    headline = (
      <>
        Ein Wort ist eine <span className="highlight">Phase aufgestiegen.</span>
      </>
    );
  } else {
    headline = (
      <>
        {word(up)} Wörter sind eine <span className="highlight">Phase aufgestiegen.</span>
      </>
    );
  }

  return (
    <div className="mx-auto max-w-xl space-y-[18px]">
      <div className="label-mono">
        Sitzung beendet · {mmss} Min · {MODE_LABEL[mode]}
      </div>

      <div className="flex items-end gap-3.5">
        <button
          onClick={() => setReplay((r) => r + 1)}
          aria-label="Zähler wiederholen"
          className="text-left text-[96px] font-extrabold leading-[0.82] tracking-[-0.06em] tabular-nums sm:text-[132px]"
        >
          {shown}
        </button>
        <div className="space-y-2 pb-1">
          <div className="text-[34px] font-extrabold leading-none text-muted-foreground">/{total}</div>
          <span className="inline-block rounded-chip border-2 border-ink bg-marker px-2 py-1 font-mono text-xs font-bold uppercase">
            {pct}% richtig
          </span>
        </div>
      </div>

      <h1 className="text-[30px] font-extrabold leading-[1.05]">{headline}</h1>

      <div className="flex flex-col items-center gap-3 sm:flex-row">
        <Karti size={180} mood={kartiResult.mood} />
        <div className="space-y-2 text-center sm:text-left">
          <p className="text-[19px] font-extrabold leading-snug">{kartiLine.text}</p>
          {kartiTip && <p className="text-sm text-body">Tipp: {kartiTip.text}</p>}
        </div>
      </div>

      <div className="ink-panel overflow-hidden">
        {answers.map((a, i) => {
          const vocab = vocabularies.find((v) => v.id === (a as any).vocabId) ?? vocabularies[i];
          const delta = a.newPhase - a.oldPhase;
          return (
            <div
              key={i}
              className="flex items-center gap-3 border-b-[1.5px] border-divider px-3.5 py-[11px] last:border-b-0"
            >
              <span className="w-12 shrink-0 truncate font-term text-[22px] font-black">{vocab?.question}</span>
              <span className="min-w-0 flex-1 truncate text-[15px] font-semibold">{a.correctAnswer}</span>
              <PhaseDots
                phase={a.newPhase}
                lostAt={delta < 0 ? a.oldPhase : undefined}
                gainedAt={delta > 0 ? a.newPhase : undefined}
                popGained={settled}
                className="hidden xs:flex"
              />
              <span
                className={cn(
                  'w-8 text-right font-mono text-xs font-bold',
                  delta < 0 && 'text-wrong-text',
                  delta === 0 && 'text-muted-foreground'
                )}
              >
                {delta > 0 ? '+1' : delta < 0 ? '−1' : '·'}
              </span>
            </div>
          );
        })}
      </div>

      <div className="space-y-2.5 pb-4">
        <Button onClick={onRetry} disabled={retrying} size="lg" className="h-[58px] w-full">
          {retrying ? 'Wird vorbereitet...' : mistakes > 0 ? `Erneut üben · ${mistakes} Fehler` : 'Erneut üben'}
        </Button>
        <Button onClick={onBack} variant="outline" className="w-full">
          Zurück zum Kurs
        </Button>
      </div>
    </div>
  );
}
